import { Injectable, signal, computed, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { TestAttempt } from '../data-models/test-attempt.model';
import { TestQuestion } from '../response-models/test-question.model';
import { TestResultSummary } from '../response-models/test-result-summary.model';
import { StartAttemptResponse } from '../response-models/start-attempt-response.model';
import { orderQuestions, toTestResultSummary } from '../utils/safety-test-utils';
import { SafetyTestService } from './safety-test.service';

/**
 * Safety test attempts: `/test-attempts[/:id[/answer|/submit|/results]]` and `/test-attempts/my-attempts`
 * (hub `user-test-attempts-controller.ts`). Questions come from `SafetyTestService` (they live under
 * `/safety-tests`), which also gets the local status updates.
 *
 * Holds the attempt being taken as signal state. Failures are recorded in `error` (the hub's message) and
 * `errorCode` (e.g. `TEST_TIME_EXPIRED` when an answer arrives after the time limit); the Promise helpers
 * resolve `null` / `false` / `[]`.
 */
@Injectable({
  providedIn: 'root'
})
export class TestAttemptService extends BaseService<TestAttempt> {
  private safetyTestService = inject(SafetyTestService);

  private currentAttemptSignal = signal<TestAttempt | null>(null);
  private currentQuestionsSignal = signal<TestQuestion[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private errorCodeSignal = signal<string | null>(null);

  readonly currentAttempt = computed(() => this.currentAttemptSignal());
  readonly currentQuestions = computed(() => this.currentQuestionsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  /** The `ApiError.code` of the last failure, for branching (never branch on `error`'s text). */
  readonly errorCode = computed(() => this.errorCodeSignal());

  constructor() {
    super('test-attempts', TestAttempt);
  }

  /** Start (or resume) an attempt and load its questions in the attempt's order; resolves `null` on failure. */
  async startTestAttempt(testId: string): Promise<StartAttemptResponse | null> {
    return this.track('Failed to start test', async () => {
      const attempt = await firstValueFrom(this.create({ testId }));
      if (!attempt) {
        return null;
      }
      const questions = orderQuestions(await this.safetyTestService.fetchQuestions(testId), attempt.questionIds);
      this.currentAttemptSignal.set(attempt);
      this.currentQuestionsSignal.set(questions);
      this.safetyTestService.markTestStatus(testId, 'in_progress');
      return { attempt, questions, resumed: attempt.resumed };
    });
  }

  /** Load an attempt and its questions into the current state; resolves `null` on failure. */
  async getAttempt(attemptId: string): Promise<TestAttempt | null> {
    try {
      const attempt = await firstValueFrom(this.get(attemptId));
      if (attempt) {
        const questions = await this.safetyTestService.fetchQuestions(attempt.testId);
        this.currentAttemptSignal.set(attempt);
        this.currentQuestionsSignal.set(orderQuestions(questions, attempt.questionIds));
      }
      return attempt;
    } catch (error: unknown) {
      console.error('Error getting attempt:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /**
   * Save an answer (it can be changed until submit). Resolves `false` on failure, with `errorCode`
   * `TEST_TIME_EXPIRED` when the attempt's time limit has passed.
   */
  async saveAnswer(attemptId: string, questionId: string, optionId: string): Promise<boolean> {
    this.clearError();
    try {
      await firstValueFrom(this.request('POST', { id: attemptId, path: 'answer' }, () => undefined, { questionId, optionId }));
      this.recordAnswer(questionId, optionId);
      return true;
    } catch (error: unknown) {
      this.recordError(error, 'Failed to save answer');
      return false;
    }
  }

  /** Submit an attempt for grading and refresh the tests; resolves `null` on failure. */
  async submitTest(attemptId: string): Promise<TestResultSummary | null> {
    return this.track('Failed to submit test', async () => {
      const result = await firstValueFrom(this.request('POST', { id: attemptId, path: 'submit' }, toTestResultSummary, {}));
      const testId = this.currentAttemptSignal()?.testId;
      this.currentAttemptSignal.set(null);
      this.currentQuestionsSignal.set([]);
      if (testId) {
        this.safetyTestService.markTestStatus(testId, result.passed ? 'passed' : 'failed', result.score);
      }
      void this.safetyTestService.loadTests();
      return result;
    });
  }

  /**
   * Detailed results of a completed attempt (`{ attempt, test, questions }`, with correct answers).
   * Resolves `null` on failure.
   */
  async getResults(attemptId: string): Promise<any | null> {
    try {
      return await firstValueFrom(this.request('GET', { id: attemptId, path: 'results' }, data => data ?? null));
    } catch (error: unknown) {
      console.error('Error getting results:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /** The signed-in user's attempts in this organization; resolves `[]` on failure. */
  async getMyAttempts(): Promise<TestAttempt[]> {
    try {
      return await firstValueFrom(this.getMany({ path: 'my-attempts' }));
    } catch (error: unknown) {
      console.error('Error getting attempts:', ApiError.fromHttpError(error).message);
      return [];
    }
  }

  /** The attempt's remaining time as `m:ss` ('Time expired' once it has run out). */
  getTimeRemaining(attempt: TestAttempt): string {
    if (!attempt.timeLimitExpiresAt) return '';
    const remainingMs = new Date(attempt.timeLimitExpiresAt).getTime() - Date.now();
    if (remainingMs <= 0) return 'Time expired';
    const minutes = Math.floor(remainingMs / 60000);
    const seconds = Math.floor((remainingMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  clearError(): void {
    this.errorSignal.set(null);
    this.errorCodeSignal.set(null);
  }

  /** Records a saved answer on a copy of the current attempt, advancing its question index. */
  private recordAnswer(questionId: string, optionId: string): void {
    this.currentAttemptSignal.update(attempt => {
      if (!attempt) return attempt;
      const copy = Object.assign(Object.create(Object.getPrototypeOf(attempt)) as TestAttempt, attempt);
      copy.answers = [
        ...attempt.answers.filter(answer => answer.questionId !== questionId),
        { questionId, selectedOptionId: optionId },
      ];
      copy.currentQuestionIndex = Math.max(attempt.currentQuestionIndex, attempt.questionIds.indexOf(questionId) + 1);
      return copy;
    });
  }

  /** Runs an action with the loading flag set; a failure is recorded and resolves `null`. */
  private async track<R>(fallback: string, action: () => Promise<R>): Promise<R | null> {
    this.loadingSignal.set(true);
    this.clearError();
    try {
      return await action();
    } catch (error: unknown) {
      this.recordError(error, fallback);
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  private recordError(error: unknown, fallback: string): void {
    const apiError = ApiError.fromHttpError(error);
    console.error(`[TestAttemptService] ${fallback}:`, apiError.message);
    this.errorSignal.set(apiError.errors[0] ?? fallback);
    this.errorCodeSignal.set(apiError.code ?? null);
  }
}
